import * as THREE from 'three';
import type { GameMap } from '../../world/maps/mapTypes';
import { FOW_GLSL, fowUniforms } from './fowShader';
import { heightTexture } from './terrain';

/**
 * Animated water: depth-tinted colour from the heightmap, shoreline foam,
 * procedural wave normals, Fresnel sky reflection and sun specular.
 * Quality levels reduce wave octaves on low-end hardware.
 */
export function buildWater(map: GameMap, quality: number, sunDir: THREE.Vector3): THREE.Mesh | null {
  let anyWater = false;
  for (let i = 0; i < map.heights.length; i++) if (map.heights[i] < 0) {
    anyWater = true;
    break;
  }
  if (!anyWater) return null;
  const pad = 40;
  const geo = new THREE.PlaneGeometry(map.size + pad * 2, map.size + pad * 2, 1, 1);
  geo.rotateX(-Math.PI / 2);
  geo.translate(map.size / 2, -0.12, map.size / 2);
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    // Pull the surface slightly towards the camera so it always wins against near-level shallows.
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -4,
    uniforms: {
      uHeight: { value: heightTexture(map) },
      uSize: { value: map.size },
      uTime: fowUniforms.uTime,
      uSun: { value: sunDir.clone().normalize() },
      uQuality: { value: quality },
      uDeep: { value: new THREE.Color(0x0e3a52) },
      uShallow: { value: new THREE.Color(0x3b8f92) },
      uSky: { value: new THREE.Color(0xa8c8e8) },
      uFowTex: fowUniforms.uFowTex,
      uFowSize: fowUniforms.uFowSize,
      uFowEnabled: fowUniforms.uFowEnabled,
    },
    vertexShader: `
      varying vec3 vWorld;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorld = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }`,
    fragmentShader: `
      uniform sampler2D uHeight;
      uniform float uSize, uTime, uQuality;
      uniform vec3 uSun, uDeep, uShallow, uSky;
      varying vec3 vWorld;
      ${FOW_GLSL}
      vec2 waveGrad(vec2 p, float t) {
        vec2 g = vec2(0.0);
        g += vec2(cos(p.x * 0.9 + t * 1.1), cos(p.y * 0.7 - t * 0.9)) * 0.08;
        g += vec2(cos(p.x * 2.3 - p.y * 1.7 + t * 1.9), cos(p.y * 2.1 + p.x * 1.3 - t * 1.6)) * 0.05;
        if (uQuality > 0.5) {
          g += vec2(cos(p.x * 5.1 + p.y * 3.3 + t * 3.1), cos(p.y * 4.7 - p.x * 3.9 + t * 2.7)) * 0.03;
          g += vec2(cos(p.x * 9.7 - p.y * 7.1 - t * 4.3), cos(p.y * 11.3 + p.x * 6.1 + t * 3.9)) * 0.015;
        }
        return g;
      }
      void main() {
        vec2 huv = (vWorld.xz + 0.5) / (uSize + 1.0);
        float inside = step(0.0, huv.x) * step(huv.x, 1.0) * step(0.0, huv.y) * step(huv.y, 1.0);
        float h = mix(-4.0, texture2D(uHeight, clamp(huv, 0.0, 1.0)).r * 16.0 - 6.0, inside);
        float depth = max(0.0, -0.12 - h);
        if (depth <= 0.0) discard;
        vec2 g = waveGrad(vWorld.xz, uTime);
        vec3 n = normalize(vec3(-g.x, 1.0, -g.y));
        vec3 viewDir = normalize(cameraPosition - vWorld);
        float fres = pow(1.0 - max(dot(n, viewDir), 0.0), 3.0);
        float dt = clamp(depth / 2.5, 0.0, 1.0);
        vec3 col = mix(uShallow, uDeep, dt);
        col = mix(col, uSky, fres * 0.55);
        vec3 hv = normalize(uSun + viewDir);
        float spec = pow(max(dot(n, hv), 0.0), 120.0) * 1.4;
        col += vec3(1.0, 0.95, 0.8) * spec;
        // Shoreline foam bands.
        float foam = smoothstep(0.35, 0.0, depth) * (0.55 + 0.45 * sin(depth * 30.0 - uTime * 2.5 + vWorld.x * 0.3));
        col = mix(col, vec3(0.92, 0.96, 0.98), clamp(foam, 0.0, 1.0) * 0.8);
        float alpha = clamp(0.55 + dt * 0.4 + foam * 0.3, 0.0, 0.95);
        col *= fowLight(vWorld);
        gl_FragColor = vec4(col, alpha);
        #include <colorspace_fragment>
      }`,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 2;
  mesh.name = 'water';
  return mesh;
}
